import React, { useRef } from 'react'; 
import { FaPrint, FaArrowLeft } from 'react-icons/fa'; 
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from "react-helmet-async";
import './DonationReceipt.css';

const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); 

const fmtDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date.getTime())) return String(d);
  // Use UTC values to avoid timezone shift
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
};

// Extract only Hindi text for Donation Type
const getHindiDonationType = (type) => {
  if (!type) return '—';
  if (type.includes('प्रोत्साहन निधि') || type.toLowerCase().includes('encouragement')) return 'प्रोत्साहन निधि';
  if (type.includes('शिक्षा नेक') || type.toLowerCase().includes('education')) return 'शिक्षा नेक';
  if (type.includes('सामान्य दान') || type.toLowerCase().includes('general')) return 'सामान्य दान';
  
  // If format is "English (Hindi)", extract Hindi part
  const match = type.match(/\(([^)]+)\)/);
  if (match) return match[1];
  
  return type;
};

const DonationReceipt = () => { 
  const location = useLocation(); 
  const navigate = useNavigate(); 
  const { donation } = location.state || {}; 
  const receiptRef = useRef(null);

  const handlePrint = () => { 
    if (!receiptRef.current) return;

    const printWindow = window.open('', '_blank', 'width=900,height=650');
    if (!printWindow) {
      alert("Please allow pop-ups to print the receipt.");
      return;
    }

    const printContent = receiptRef.current.innerHTML;
    let stylesHtml = '';
    for (const stylesheet of document.styleSheets) {
      try {
        let rules = '';
        for (const rule of stylesheet.cssRules) { rules += rule.cssText; }
        stylesHtml += `<style>${rules}</style>`;
      } catch (e) {
        if (stylesheet.href) { stylesHtml += `<link rel="stylesheet" href="${stylesheet.href}">`; }
      }
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>Receipt_${donation?.receiptNo || 'Print'}</title>
          ${stylesHtml}
          <style>
            @media print {
              @page { size: A5 landscape; margin: 0; }
              html, body { margin: 0 !important; padding: 0 !important; width: 210mm !important; height: 148mm !important; }
              .donation-receipt-a5-container { width: 210mm !important; height: 148mm !important; box-shadow: none !important; margin: 0 !important; }
              .rsk-receipt-border { outline: 1px solid #000 !important; outline-offset: -5px !important; }
              -webkit-print-color-adjust: exact !important; 
              print-color-adjust: exact !important;
            }
          </style>
        </head>
        <body>
          <div class="donation-receipt-wrapper" style="width:100%; height:100%; margin:0; padding:0;">
            ${printContent}
          </div>
          <script>
            window.onload = function() { setTimeout(function() { window.print(); window.close(); }, 400); };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (!donation) { 
    return (
      <div className="donation-receipt-wrapper">
        <button className="donation-receipt-btn donation-receipt-btn-back" onClick={() => navigate(-1)}> <FaArrowLeft/> Back </button>
        <p style={{ textAlign: 'center', color: '#000', marginTop: '40px' }}>No receipt data found.</p>
      </div>
    ); 
  }

  // Transaction logic
  const isOnline = donation.mode === 'Online Gateway' || donation.pgPaymentMode || (donation.receiptNo && donation.receiptNo.includes('ONL'));
  let transactionDetails = donation.refNo && donation.refNo !== 'N/A' ? donation.refNo : '';
  let paymentModeDisplay = isOnline ? 'Online Gateway' : (donation.mode || 'CASH');

  return ( 
    <>
    <Helmet>
                <title>Donation Receipt | Rajput Shiksha Kosh</title>
        
                <meta
                  name="description"
                  content="View and print your donation receipt for the Rajput Shiksha Kosh organization."
                />
              </Helmet>
    <div className="donation-receipt-wrapper">
      <div className="donation-receipt-actions">
        <button className="donation-receipt-btn donation-receipt-btn-back" onClick={() => navigate(-1)}> <FaArrowLeft/> Back </button>
        <button className="donation-receipt-btn donation-receipt-btn-print" onClick={handlePrint}> <FaPrint/> Print Receipt </button>
      </div>

      <div ref={receiptRef} className="donation-receipt-a5-container">
        <div className="rsk-receipt-border">
          
          {/* Top PAN and Reg No */}
          <div className="rsk-top-bar">
            <span>PAN - AADTR3478A</span>
            <span>रजि. नं. 05/2018/जोधपुर</span>
          </div>

          <hr className="rsk-hr" />

          {/* Main Huge Title */}
          <div className="rsk-main-title-block">
            <h1 className="rsk-title">राजपूत शिक्षा कोष</h1>
            <p className="rsk-subtitle">(प्रतिभावान जरूरतमंद बालक/बालिकाओं की शिक्षा व्यवस्था हेतु)</p>
          </div>

          <hr className="rsk-hr" />

          {/* Address and Tax Notice */}
          <div className="rsk-address-block">
            <p className="rsk-address">कार्यालय - मारवाड़ राजपूत सभा भवन, पावटा 'बी' रोड़, जोधपुर (राज.)</p>
            <p className="rsk-tax-note">(दान राशि पर आयकर एक्ट की धारा 80G के तहत छुट)</p>
          </div>

          {/* Receipt No and Date */}
          <div className="rsk-meta-row">
            <span className="rsk-meta-text">रसीद नं. <span className="rsk-dynamic-value">{donation.receiptNo || '—'}</span></span>
            <span className="rsk-meta-text">दिनांक <span className="rsk-dynamic-value">{fmtDate(donation.date)}</span></span>
          </div>

          {/* Donor Details Body in Hindi */}
          <div className="rsk-body">
            
            {/* Row 1: Name and Father's Name */}
            <div className="rsk-field-row">
              <div className="rsk-field-half">
                <span className="rsk-label">शुभ नाम :</span>
                <span className="rsk-line rsk-bold">{donation.name || '—'}</span>
              </div>
              <div className="rsk-field-half">
                <span className="rsk-label">पिता का नाम :</span>
                <span className="rsk-line">{donation.fatherName || '—'}</span>
              </div>
            </div>

            {/* Row 2: Contact and Donation Type */}
            <div className="rsk-field-row">
              <div className="rsk-field-half">
                <span className="rsk-label">मो.नं. :</span>
                <span className="rsk-line">{donation.mobileNo || '—'}</span>
              </div>
              <div className="rsk-field-half">
                <span className="rsk-label">बाबत :</span>
                <span className="rsk-line rsk-bold">{getHindiDonationType(donation.donationType)}</span>
              </div>
            </div>

            {/* Row 3: Payment Mode (Centered text on line) */}
            <div className="rsk-field-row" style={{ marginTop: '5px' }}>
              <span className="rsk-label">मार्फत :</span>
              <span className="rsk-line rsk-line-center">
                {paymentModeDisplay} {transactionDetails ? `- ${transactionDetails}` : ''} 
              </span>
              <span className="rsk-label-suffix">सधन्यवाद प्राप्त हुए।</span>
            </div>

          </div>

          {/* Amount Box (Left) and 80G Info (Right Center) */}
          <div className="rsk-mid-section">
            <div className="rsk-amount-box">
              ₹ {fmt(donation.amount)} /-
            </div>
            
            <div className="rsk-80g-box">
              <p className="rsk-80g-title">Income Tax 80G की Authority</p>
              <p className="rsk-80g-text">DIN NO. AADTR3478AF2021101 JAIPUR/80G/AY 2022-23 TO AY 2026-27</p>
              <p className="rsk-80g-text">DATED: 06-04-2022, PAN: AADTR3478A</p>
              <p className="rsk-80g-web">website: www.rajputshikshakosh.com | Email: info@rajputshikshakosh.com, rajputshikshakosh@gmail.com</p>
            </div>
          </div>

          <hr className="rsk-hr" />

          {/* Bank Details Section */}
          <div className="rsk-bank-section">
            <div className="rsk-bank-header">Bank Details</div>
            
            <div className="rsk-bank-grid">
              {/* SBI Block - strictly 2 lines */}
              <div className="rsk-bank-col">
                <div>Bank Name - <span className="rsk-bank-val">State Bank of India</span> A/C Name - <span className="rsk-bank-val">Rajput Shiksha Kosh</span></div>
                <div>A/C No - <span className="rsk-bank-val">37561552700</span>, Branch - <span className="rsk-bank-val">Mandore Road, Jodhpur</span>, IFSC - <span className="rsk-bank-val">SBIN0031374</span></div>
              </div>
              
              {/* ICICI Block - strictly 2 lines */}
              <div className="rsk-bank-col">
                <div>Bank Name - <span className="rsk-bank-val">ICICI Bank</span> A/C Name - <span className="rsk-bank-val">Rajput Shiksha Kosh</span></div>
                <div>A/C No. - <span className="rsk-bank-val">682801701715</span> Branch - <span className="rsk-bank-val">Paota, Jodhpur</span> IFSC - <span className="rsk-bank-val">ICIC0006828</span></div>
              </div>
            </div>
          </div>

          {/* Centered System Generated Message */}
          <div className="rsk-footer-section">
            <div className="rsk-system-msg">
              This is a system generated receipt hence no authorized signature required.
            </div>
          </div>

        </div>
      </div>
    </div>
    </>
  ); 
};

export default DonationReceipt;